import { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import './marketing.css';
import { apps, categories } from './data/apps';
import {
  filterApps,
  parseInitialState,
  sortApps,
  syncUrl
} from './utils/catalog';
import Header from './components/Header';
import Hero from './components/Hero';
import AiStory from './components/AiStory';
import CategoryChips from './components/CategoryChips';
import AppCard from './components/AppCard';
import AppDetailModal from './components/AppDetailModal';
import { EmptyState, SkeletonGrid } from './components/CatalogStates';
import Footer from './components/Footer';

function App() {
  const initial = useMemo(parseInitialState, []);
  const [state, setState] = useState(initial);
  const [inputValue, setInputValue] = useState(initial.query);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const searchRef = useRef(null);

  const update = (changes, resetPage = true) => {
    setState((current) => ({
      ...current,
      ...changes,
      page: resetPage ? 1 : current.page
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => update({ query: inputValue }), 200);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => syncUrl(state), [state]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const shortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      const slash = event.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName);

      if (shortcut || slash) {
        event.preventDefault();
        searchRef.current?.focus();
      }

      if (event.key === 'Escape' && document.activeElement === searchRef.current) {
        setInputValue('');
        searchRef.current.blur();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const filtered = useMemo(() => filterApps(apps, state), [state]);
  const ordered = useMemo(
    () => sortApps(filtered, state.sort, state.tab),
    [filtered, state.sort, state.tab]
  );
  const visible = ordered.slice(0, state.page * 12);

  const categoryBase = useMemo(
    () => filterApps(apps, state, 'category'),
    [state]
  );

  const categoryCounts = Object.fromEntries(
    ['All', ...categories].map((category) => [
      category,
      category === 'All'
        ? categoryBase.length
        : categoryBase.filter((app) => app.category === category).length
    ])
  );

  const clearAll = () => {
    setInputValue('');
    update({ query: '', category: 'All' });
  };

  return (
    <>
      <Header onSearch={() => searchRef.current?.focus()} />
      <main>
        <div className="page-shell">
          <Hero
            inputValue={inputValue}
            onInput={setInputValue}
            inputRef={searchRef}
            shippedCount={apps.length}
          />
          <AiStory />
          <CategoryChips
            categories={categories}
            active={state.category}
            counts={categoryCounts}
            onChange={(category) => update({ category })}
          />

          <section id="directory" className="directory">
            <div className="catalog-content catalog-solo">
              {loading ? (
                <SkeletonGrid />
              ) : ordered.length === 0 ? (
                <EmptyState onClear={clearAll} />
              ) : (
                <>
                  <div className="app-grid">
                    {visible.map((app) => (
                      <AppCard
                        app={app}
                        query={state.query}
                        onOpen={setSelectedApp}
                        key={app.id}
                      />
                    ))}
                  </div>

                  <div className="load-more">
                    {visible.length < ordered.length && (
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => setState((current) => ({
                          ...current,
                          page: current.page + 1
                        }))}
                      >
                        Load more
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </main>

      <AppDetailModal app={selectedApp} onClose={() => setSelectedApp(null)} />
      <Footer />
    </>
  );
}

export default App;
